import CategoryFilterModal from "@/src/components/action-modals/category-filter";
import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import EditCategoryModal from "@/src/components/action-modals/edit-category";
import CategoryRow from "@/src/components/dashboard/category-row";
import ScPagination from "@/src/components/ui-kit/pagination";
import type { Category, CategoryFilters } from "@/src/models/category.model";
import {
  useDeleteCategoryMutation,
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
} from "@/src/services/api/category/category.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Text, View } from "react-native";
import CategoriesEmptyState from "./empty-state";

const DEBOUNCE_DELAY_MS = 500;

type CategoriesTableProps = Readonly<{
  searchQuery: string;
  onFilterCountChange?: (count: number) => void;
  onTotalCountChange?: (count: number) => void;
  filterTrigger?: boolean;
  onFilterTriggerConsumed?: () => void;
}>;

const CategoriesTable = ({
  searchQuery,
  onFilterCountChange,
  onTotalCountChange,
  filterTrigger,
  onFilterTriggerConsumed,
}: CategoriesTableProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(
    null,
  );
  const [activeFilters, setActiveFilters] = useState<CategoryFilters>({
    tag: null,
  });

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (filterTrigger) {
      setIsFilterVisible(true);
      onFilterTriggerConsumed?.();
    }
  }, [filterTrigger]);

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetCategoriesQuery({
    pageNo: currentPage,
    search: debouncedSearch,
    tag: activeFilters.tag,
  });

  const { mutate: updateCategory, isPending: isUpdating } =
    useUpdateCategoryMutation();
  const { mutate: deleteCategory, isPending: isDeleting } =
    useDeleteCategoryMutation();

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, DEBOUNCE_DELAY_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    onTotalCountChange?.(categoriesData?.totalCount ?? 0);
  }, [categoriesData?.totalCount]);

  const handleApplyFilters = (filters: CategoryFilters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
    const activeCount = Object.values(filters).filter(Boolean).length;
    onFilterCountChange?.(activeCount);
  };

  const handleSaveEdit = (categoryId: string, categoryName: string) => {
    const categoryFilter = JSON.stringify({ ItemId: categoryId });
    updateCategory(
      { filter: categoryFilter, categoryName },
      { onSuccess: () => setEditingCategory(null) },
    );
  };

  const handleConfirmDelete = () => {
    if (!deletingCategory) return;
    const categoryFilter = JSON.stringify({ ItemId: deletingCategory?.ItemId });
    deleteCategory(
      { filter: categoryFilter },
      { onSuccess: () => setDeletingCategory(null) },
    );
  };

  if (isCategoriesLoading) {
    return (
      <View className="flex-1 justify-center items-center p-4">
        <ActivityIndicator
          size="large"
          color={themeVars["--color-primary-default"] as string}
        />
      </View>
    );
  }

  if (isCategoriesError) {
    return (
      <View className="flex-1 justify-center items-center p-4">
        <Text className="text-crimson text-sm">
          {t("ERROR_LOADING_CATEGORIES")}
        </Text>
      </View>
    );
  }

  return (
    <>
      <View className="gap-1">
        <View className="flex-row items-center py-2 border-b border-ash-3">
          <View className="flex-1">
            <Text className="text-xs text-onyx font-medium tracking-wide">
              {t("LABEL_NAME")}
            </Text>
          </View>
          <View className="flex-[0.4]">
            <Text className="text-xs text-onyx font-medium tracking-wide">
              {t("LABEL_TAGS")}
            </Text>
          </View>
          <View className="w-10 items-center">
            <Text className="text-xs text-onyx font-medium tracking-wide">
              {t("LABEL_ACTIONS")}
            </Text>
          </View>
        </View>

        {/* Table Rows */}
        {categoriesData?.items && categoriesData.items.length > 0 ? (
          <>
            {categoriesData.items.map((category, index) => (
              <CategoryRow
                key={category?.ItemId}
                category={category}
                isLast={index === (categoriesData.items?.length ?? 0) - 1}
                onEdit={setEditingCategory}
                onDelete={setDeletingCategory}
              />
            ))}

            <ScPagination
              currentPage={currentPage}
              totalPages={categoriesData?.totalPages ?? 1}
              totalItems={categoriesData?.totalCount ?? 0}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <CategoriesEmptyState />
        )}
      </View>

      <CategoryFilterModal
        visible={isFilterVisible}
        onClose={() => setIsFilterVisible(false)}
        onApply={handleApplyFilters}
      />
      <EditCategoryModal
        visible={editingCategory !== null}
        category={editingCategory}
        onSave={handleSaveEdit}
        onCancel={() => setEditingCategory(null)}
        loading={isUpdating}
      />
      <ConfirmModal
        visible={deletingCategory !== null}
        title={t("QUEST_DELETE_CATEGORY", {
          name: deletingCategory?.categoryName,
        })}
        description={t("LABEL_DESC_DELETE_CATEGORY")}
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onCancel={() => setDeletingCategory(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </>
  );
};

export default CategoriesTable;
